import React, { useState } from "react";

import MuiMenu, { MenuProps as MuiMenuProps } from "@mui/material/Menu";
import isElement from "lodash/isElement";

import MenuOptionItem, { MenuOption } from "./MenuOption";

import styles from "./Menu.module.css";

interface MenuProps
  extends Omit<
    MuiMenuProps,
    "open" | "anchorEl" | "onClick" | "onClose" | "children"
  > {
  children: (props: {
    isMenuOpen: boolean;
    handleOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  }) => React.ReactNode;
  options: MenuOption[];
  transformOrigin?: MuiMenuProps["transformOrigin"];
  anchorOrigin?: MuiMenuProps["anchorOrigin"];
}

export default function Menu({
  children,
  options,
  anchorOrigin = { horizontal: "left", vertical: "bottom" },
  transformOrigin = { horizontal: "center", vertical: "top" },
  ...restProps
}: MenuProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = isElement(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      {children({ isMenuOpen, handleOpenMenu })}

      <MuiMenu
        classes={{ paper: styles.menuPaper }}
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        autoFocus={false}
        PaperProps={{ elevation: 0 }}
        MenuListProps={{ className: styles.menuList }}
        {...restProps}
      >
        {options.map((option) => (
          <MenuOptionItem
            key={option.label}
            handleCloseMenu={handleCloseMenu}
            {...option}
          />
        ))}
      </MuiMenu>
    </>
  );
}
